import { useEffect, useMemo, useRef } from 'react';

import { DealerWithMarkerState } from '@/components/sections/dealer';
import { DealerResultDataListItem } from '@/components/sections/dealer/result/data-list/item';
import {
  ScrollArea,
  ScrollAreaCorner,
  ScrollAreaScrollbar,
  ScrollAreaThumb,
  ScrollAreaViewport,
} from '@/components/shared/element/scroll-area';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Flex } from '@/components/shared/layout/flex';
import { getDistance } from '@/components/shared/utility/location';
import { IDealer } from '@/types/dealer/dealer';
import { ILocation } from '@/types/dealer/location';

export interface DealerResultDataListProps {
  dealers: IDealer[];
  selectedDealer?: DealerWithMarkerState;
  currentLocation?: ILocation;
  onOpenDrawer: (uid: string) => void;
  vehicleType: string;
}

export const DealerResultDataList: React.FunctionComponent<
  DealerResultDataListProps
> = (props: DealerResultDataListProps) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const sortedDealer = useMemo(() => {
    if (props.currentLocation) {
      return props.dealers.sort((a, b) => {
        return (
          getDistance(
            Number(props.currentLocation?.lat),
            Number(props.currentLocation?.lng),
            Number(a.sale?.lat),
            Number(a.sale?.lon)
          ) -
          getDistance(
            Number(props.currentLocation?.lat),
            Number(props.currentLocation?.lng),
            Number(b.sale?.lat),
            Number(b.sale?.lon)
          )
        );
      });
    }
    return props.dealers.sort((a: IDealer, b: IDealer) =>
      (a?.mirai?.name_th ?? '').localeCompare(b?.mirai?.name_th ?? '', 'th')
    );
  }, [props.dealers, props.currentLocation]);

  useEffect(() => {
    if (!props.selectedDealer) return;
    if (!props.selectedDealer.isSelectFromMarker) return;
    if (!viewportRef.current) return;

    const el = document.getElementById(`dealer_${props.selectedDealer.uid}`);
    if (!el) return;

    viewportRef.current.scrollTo({
      top:
        el.offsetTop - viewportRef.current.clientHeight / 2 + el.clientHeight,
      behavior: 'smooth',
    });
  }, [props.selectedDealer]);

  return (
    <Flex
      data-test="dealer_list"
      direction="column"
      css={{
        height: '100%',
        borderBottom: '1px solid $gray300',
      }}
    >
      <Box
        backgroundColor="gray200"
        borderColor="gray300"
        paddingX={{
          '@initial': '6',
          '@sm': '7',
        }}
        paddingY={{
          '@initial': '3',
          '@sm': '4',
        }}
        display={{ '@initial': 'none', '@lg': 'block' }}
        css={{
          borderBottomWidth: '2px',
          borderBottomStyle: 'solid',
        }}
      >
        <Flex alignItems="center" justifyContent="center" gap="2">
          <Text as="p" font="bold" css={{ marginBottom: -2 }}>
            รายชื่อ {`(${props.dealers.length})`}
          </Text>
        </Flex>
      </Box>
      {!!props.dealers && (
        <Box data-test="dealer_list" height="full" overflow="hidden">
          <ScrollArea type="auto">
            <ScrollAreaViewport ref={viewportRef}>
              {sortedDealer.map((o, i) => (
                <DealerResultDataListItem
                  key={`dealerResultItem${i}`}
                  item={o}
                  vehicleType={props.vehicleType}
                  onOpenDrawer={props.onOpenDrawer}
                />
              ))}
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical">
              <ScrollAreaThumb />
            </ScrollAreaScrollbar>
            <ScrollAreaCorner />
          </ScrollArea>
        </Box>
      )}
    </Flex>
  );
};

DealerResultDataList.displayName = 'SectionDealerResultDataList';
